import { ThemeMode } from '@apps-common/config/theme'
import { useTheme } from '@apps-common/hooks/useTheme'

/* -------------------------------------
 * Pastel palettes (avatar backgrounds)
 * ----------------------------------- */

const LIGHT_PASTEL_COLORS = [
  '#FDE2E2',
  '#FFE8D6',
  '#FEF3C7',
  '#ECFDF3',
  '#D1FAE5',
  '#CCFBF1',
  '#E0F2FE',
  '#DBEAFE',
  '#E0E7FF',
  '#EDE9FE',
  '#F3E8FF',
  '#FCE7F3',
  '#FAE8FF',
  '#FFF7ED'
]

const DARK_PASTEL_COLORS = [
  '#3F1D1D',
  '#422006',
  '#3A2E0A',
  '#064E3B',
  '#065F46',
  '#134E4A',
  '#164E63',
  '#1E3A8A',
  '#312E81',
  '#4C1D95',
  '#581C87',
  '#701A75',
  '#5B134E',
  '#292524'
]

/* -------------------------------------
 * Helpers
 * ----------------------------------- */

const hashCode = (s: string) =>
  s.split('').reduce((a, b) => {
    // eslint-disable-next-line no-bitwise
    a = (a << 5) - a + b.charCodeAt(0)
    return a & a
  }, 0)

const mod = (n: number, m: number) => ((n % m) + m) % m

const pickColor = (palette: string[], seed: string) => palette[mod(hashCode(seed), palette.length)] ?? palette[0]

const hexToRgb = (hex: string) => {
  const h = hex.replace('#', '')
  const n = parseInt(h, 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

const luminance = (hex: string) => {
  const { r, g, b } = hexToRgb(hex)
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
}

const shift = (hex: string, amt: number) => {
  const { r, g, b } = hexToRgb(hex)
  const clamp = (v: number) => Math.max(0, Math.min(255, v))
  return `rgb(${clamp(r + amt)}, ${clamp(g + amt)}, ${clamp(b + amt)})`
}

/* -------------------------------------
 * Public API
 * ----------------------------------- */

export interface AvatarColors {
  backgroundColor: string
  color: string
  borderColor: string
}

export const getAvatarColors = (seed: string, dark: boolean): AvatarColors => {
  const bg = pickColor(dark ? DARK_PASTEL_COLORS : LIGHT_PASTEL_COLORS, seed)
  const isLightBg = luminance(bg) > 0.65

  return {
    backgroundColor: bg,

    // Text always readable
    color: isLightBg ? '#1F2937' : '#F9FAFB', // slate-800 / near-white

    // Border is ALWAYS darker than background
    borderColor: dark
      ? shift(bg, -38) // stronger separation in dark mode
      : shift(bg, -28) // subtle but visible in light mode
  }
}

/* -------------------------------------
 * Theme-aware hook
 * ----------------------------------- */

export const useAvatarColors = () => {
  const { isDarkMode, themeMode } = useTheme()
  const dark = themeMode === ThemeMode.dark || (themeMode === ThemeMode.system && isDarkMode) || false

  return {
    getAvatarColors: (seed: string) => getAvatarColors(seed, dark)
  }
}
